import {
  Colis,
  RtoBulkScanReturn,
  RtoBulkScannedColisItem,
} from "@/interfaces/Return";

interface RtoBulkColisMatch {
  return_: RtoBulkScanReturn;
  colis: Colis;
}

export const findColisInReturns = (
  returns: RtoBulkScanReturn[],
  colisCode: string
): RtoBulkColisMatch | null => {
  for (const return_ of returns) {
    const colis = return_.colis?.find((item) => item.colisCode === colisCode);
    if (colis) {
      return { return_, colis };
    }
  }
  return null;
};

export const isColisScanned = (
  scannedColisItems: RtoBulkScannedColisItem[],
  colisCode: string
): boolean => scannedColisItems.some((item) => item.colisCode === colisCode);

export const getTotalColisCount = (colisList: RtoBulkScanReturn[]): number =>
  colisList.reduce((sum, return_) => sum + (return_.colis?.length ?? 0), 0);

export type ReturnScanStatus = "not_started" | "in_progress" | "complete";

export const buildScannedCountByTournee = (
  scannedColisItems: RtoBulkScannedColisItem[]
): Map<number, number> => {
  const counts = new Map<number, number>();

  scannedColisItems.forEach((item) => {
    counts.set(item.numTournee, (counts.get(item.numTournee) ?? 0) + 1);
  });

  return counts;
};

export const getReturnColisProgress = (
  return_: RtoBulkScanReturn,
  scannedCountByTournee: Map<number, number>
) => {
  const expected = return_.colis?.length ?? 0;
  const scanned = scannedCountByTournee.get(return_.numTournee) ?? 0;
  const isComplete = expected === 0 || scanned === expected;
  const status: ReturnScanStatus = isComplete
    ? "complete"
    : scanned > 0
      ? "in_progress"
      : "not_started";

  return {
    scanned,
    expected,
    isComplete,
    status,
  };
};

export const getScannedColisForReturn = (
  return_: RtoBulkScanReturn,
  scannedColisItems: RtoBulkScannedColisItem[]
): Colis[] => {
  const colisByCode = new Map(
    return_.colis?.map((colis) => [colis.colisCode, colis]) ?? []
  );

  return scannedColisItems
    .filter((item) => item.numTournee === return_.numTournee)
    .map((item) => colisByCode.get(item.colisCode))
    .filter((colis): colis is Colis => colis !== undefined);
};

export const getRemainingColisForReturn = (
  return_: RtoBulkScanReturn,
  scannedColisItems: RtoBulkScannedColisItem[]
): Colis[] => {
  const scannedCodes = new Set(
    scannedColisItems
      .filter((item) => item.numTournee === return_.numTournee)
      .map((item) => item.colisCode)
  );

  return return_.colis?.filter((colis) => !scannedCodes.has(colis.colisCode)) ?? [];
};

export const isScanSessionComplete = (
  colisList: RtoBulkScanReturn[],
  scannedColisItems: RtoBulkScannedColisItem[]
): boolean => {
  const expectedColisCount = getTotalColisCount(colisList);
  if (expectedColisCount === 0) return false;

  return scannedColisItems.length === expectedColisCount;
};
